import { getPositiveAmenitiesEvent } from '../getPositiveAmenities';
import { getIsGreatPrice } from '../is-great-price';

import { CUG_TYPE, PANEL_POSITION, ROOM_TYPE } from './constants';
import { getYearMonthDay } from './get-stay-field';
import { isLowestPrice } from './is-lowest-price';

import type { HotelCard } from '@shared/types/frontend/hotel-card';
import type { Maybe, Nullable, Stay } from '@shared/types/utils';

import { parseDate } from '@shared/utils-universal/machine-date-utils';

type HotelsSeenMessage = {
  hotel: HotelCard;
  currency: string;
  hotelPosition: number;
  pageIndex: number | undefined;
  searchId: string | undefined;
  traceInfo?: Nullable<string>;
  upSortHotels?: string;
  audienceId?: Nullable<string>;
  stay?: Stay;
  correlationId: string;
  isCPB?: boolean;
  groupName?: string;
  campaignName?: string;
  hotelCardArea?: string;
  isCheaperThanUsual?: boolean;
};

export const HOTEL_PRICE_SEEN_MINIEVENT_NAME = 'HotelsFrontendHotelsSeen';
export const HOTEL_PRICE_SEEN_SCHEMA_NAME = 'hotels_seen.HotelsSeenEvent';

export const buildHotelSeenInfo = ({
  audienceId,
  campaignName,
  correlationId,
  currency,
  groupName,
  hotel,
  hotelCardArea,
  hotelPosition,
  isCPB,
  isCheaperThanUsual = false,
  pageIndex = 0,
  searchId,
  stay,
  traceInfo,
  upSortHotels,
}: HotelsSeenMessage) => {
  const {
    allPricesInfo: { prices } = {},
    id,
    priceInfo: {
      basePrice,
      cug,
      greatPricePercentage,
      partnerId,
      price,
      taxesAndFees,
    } = {},
  } = hotel;

  const cugType = cug?.type;
  const discountPercentage = cug?.discountPercentage;
  const mainPrice = prices?.[0];
  const amenities = mainPrice?.amenities;

  const positiveAmenitiesEvent =
    amenities && getPositiveAmenitiesEvent(amenities);

  const isGreatPrice = getIsGreatPrice({
    greatPricePercentage,
    isHeroPrice: true,
  });

  const isUpSortHotel = upSortHotels
    ? upSortHotels.split(',')[0] === id
    : false;

  const totalTaxesAndFees = taxesAndFees?.reduce(
    (acc, cur) => acc + cur.total,
    0,
  );

  const hasAISummary = (hotel.llmSummary?.summaryPhrases?.length ?? 0) > 0;
  const data: { [key: string]: any } = {
    hotel_id: id,
    search_id: searchId,
    ranking: hotelPosition,
    hotel_sorting_page_num: pageIndex + 1,
    has_cug: !!discountPercentage,
    cug_type:
      CUG_TYPE[cugType?.toUpperCase?.() as string] || CUG_TYPE.UNSET_CUG_TYPE,
    cug_percentage: discountPercentage ?? 0,
    is_up_sort_hotel: isUpSortHotel,
    partner_id: partnerId,
    price: price
      ? {
          currency,
          amount: Math.round(price),
          unit: 'WHOLE',
        }
      : undefined,
    trace_info: isUpSortHotel ? traceInfo : null,
    is_lowest: isLowestPrice(hotel),
    audience_id: isUpSortHotel ? audienceId : null,
    correlation_id: correlationId,
    base_price: basePrice
      ? {
          currency,
          amount: Math.floor(basePrice),
          unit: 'WHOLE',
        }
      : undefined,
    tax_fees:
      typeof totalTaxesAndFees !== 'undefined'
        ? {
            currency,
            amount: Math.floor(totalTaxesAndFees),
            unit: 'WHOLE',
          }
        : undefined,
    is_cug_area_available: true,
    is_great_price: isGreatPrice,
    is_amenity_area_available: true,
    positive_amenity_values: positiveAmenitiesEvent,
    is_cpb: isCPB,
    group_name: groupName,
    campaign_name: campaignName,
    has_ai_summary: hasAISummary,
    tags: mainPrice?.tags?.join(','),
    hotel_card_area: hotelCardArea,
    is_cheaper_than_usual: isCheaperThanUsual,
  };
  if (stay) {
    const { checkIn, checkOut, childrenAges, numberOfAdults, numberOfRooms } =
      stay;

    childrenAges.forEach((childAge, index) => {
      data[`age_of_child_${index + 1}`] = Number(childAge);
    });
    data.checkin_date = getYearMonthDay(parseDate(checkIn));
    data.checkout_date = getYearMonthDay(parseDate(checkOut));
    data.number_of_rooms = numberOfRooms;
    data.number_of_adults = numberOfAdults;
    data.number_of_children = childrenAges.length;
  }

  return { hotel_seen_info: data };
};

export const buildPriceSeenInfo = ({
  basePrice,
  campaignName,
  correlationId,
  cugType,
  currency,
  dayviewCorrelationId,
  discountPercentage,
  groupName,
  hotelId,
  isAmenityAreaAvailable,
  isCPB,
  isCugAreaAvailable,
  isDbook,
  isFiltered,
  isGreatPrice,
  isLowest,
  isLowestForHotel,
  originalRoomName,
  panelPosition,
  partnerId,
  partnerRanking,
  positiveAmenities,
  price,
  priceDiff,
  requestId,
  roomName,
  roomNameDimensions,
  roomType,
  searchId,
  tags,
  taxFees,
}: {
  panelPosition: string;
  cugType?: string;
  currency?: string;
  isDbook: boolean;
  isFiltered: boolean;
  isLowest: boolean;
  partnerId?: string;
  partnerRanking?: number;
  price?: number;
  correlationId: Maybe<string>;
  dayviewCorrelationId: Maybe<string>;
  hotelId: string;
  basePrice?: number;
  taxFees?: number;
  searchId?: Nullable<string>;
  requestId: Maybe<string>;
  isGreatPrice?: boolean;
  discountPercentage?: Nullable<number>;
  isCugAreaAvailable: boolean;
  isAmenityAreaAvailable: boolean;
  positiveAmenities?: string;
  isCPB?: boolean;
  groupName?: string;
  campaignName?: string;
  priceDiff?: number;
  roomName?: Nullable<string>;
  roomType?: Nullable<string>;
  isLowestForHotel?: boolean;
  tags?: string[];
  roomNameDimensions?: {
    bedType?: string;
    roomCategory?: string;
    lodgingType?: string;
    familyFriendly?: boolean;
  };
  originalRoomName?: Nullable<string>;
}) => ({
  price_seen_info: {
    hotel_id: hotelId && parseInt(hotelId, 10),
    is_dbook: isDbook,
    is_lowest: isLowest,
    is_lowest_for_hotel: isLowestForHotel,
    is_filtered: isFiltered,
    partner_ranking: partnerRanking,
    partner_id: partnerId,
    panel_position: PANEL_POSITION[panelPosition],
    cug_type:
      CUG_TYPE[cugType?.toUpperCase?.() as string] || CUG_TYPE.UNSET_CUG_TYPE,
    correlation_id: correlationId,
    day_view_correlation_id: dayviewCorrelationId,
    search_id: searchId,
    request_id: requestId,
    is_cug_area_available: isCugAreaAvailable,
    has_cug: !!discountPercentage,
    cug_percentage: discountPercentage ?? 0,
    positive_amenity_values: positiveAmenities,
    is_amenity_area_available: isAmenityAreaAvailable,
    is_great_price: isGreatPrice,
    is_cpb: isCPB,
    room_name: roomName,
    room_type: roomType
      ? ROOM_TYPE[roomType.toUpperCase()]
      : ROOM_TYPE.UNSET_ROOM_TYPE,
    bed_type: roomType,
    room_name_dimensions: roomNameDimensions
      ? {
          bed_type: roomNameDimensions.bedType,
          room_category: roomNameDimensions.roomCategory,
          lodging_type: roomNameDimensions.lodgingType,
          is_family_friendly: roomNameDimensions.familyFriendly,
        }
      : undefined,
    original_room_name: originalRoomName,
    group_name: groupName,
    campaign_name: campaignName,
    price: price
      ? { currency, amount: Math.round(price), unit: 'WHOLE' }
      : undefined,
    base_price: basePrice
      ? {
          currency,
          amount: Math.floor(basePrice),
          unit: 'WHOLE',
        }
      : undefined,
    tax_fees:
      typeof taxFees !== 'undefined'
        ? { currency, amount: Math.floor(taxFees), unit: 'WHOLE' }
        : undefined,
    price_vs_cheapest:
      typeof priceDiff !== 'undefined'
        ? {
            currency,
            amount: Math.round(priceDiff),
            unit: 'WHOLE',
          }
        : undefined,
    tags: tags?.join(','),
  },
});
